import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { dateTime, money, moneyExact, percent } from '../lib/format.js'
import { Badge, Card, Empty, RowsSkeleton, Stat, Tabs } from './ui.js'
import { CartIcon, CheckCircleIcon, RupeeIcon, WhatsAppIcon } from './icons.js'

interface Counts {
  sent: number
  notSent: number
  clicked: number
  ordered: number
  revenue: string
  spent: string
  clickRate: number | null
  orderRate: number | null
}

interface Message {
  id: string
  sentAt: string | null
  status: string
  phone: string | null
  clicked: boolean
  clicks: number
  clickedAt: string | null
  ordered: boolean
  orderNumber: string | null
  orderValue: string | null
  link: string | null
  charged: string
  store?: string
}

interface StoreRow extends Counts {
  shopId: string
  store: string
  domain: string
}

interface ShopFunnel extends Counts {
  messages: Message[]
}

interface PlatformFunnel {
  totals: Counts
  stores: StoreRow[]
  messages: Message[]
}

/**
 * Abandoned-checkout reminders, end to end: how many were sent, how many shoppers opened the
 * link, how many went on to order, and the latest messages with the checkout link each carried.
 *
 * One component for both screens. `scope="shop"` is a merchant's own store; `scope="platform"`
 * is the operator's view across every store, with a row per store.
 */
export function ReminderFunnelCard({ scope, shopId }: { scope: 'shop' | 'platform'; shopId?: string }): ReactNode {
  const [days, setDays] = useState<'7' | '30' | '90'>('30')

  const { data, isPending, error } = useQuery({
    queryKey: ['reminder-funnel', scope, shopId, days],
    queryFn: async (): Promise<PlatformFunnel> => {
      if (scope === 'platform') return api<PlatformFunnel>(`/v1/admin/whatsapp/abandoned-funnel?days=${days}`)

      const { messages, ...totals } = await api<ShopFunnel>(`/v1/abandoned/funnel?days=${days}`)

      return { totals, stores: [], messages }
    },
  })

  const totals = data?.totals ?? null
  const messages = data?.messages ?? []
  const stores = data?.stores ?? []

  return (
    <Card
      title="Reminder tracking"
      subtitle="Every abandoned-checkout WhatsApp reminder: sent, opened, ordered."
      action={
        <Tabs
          value={days}
          onChange={setDays}
          tabs={[
            { key: '7', label: '7 days' },
            { key: '30', label: '30 days' },
            { key: '90', label: '90 days' },
          ]}
        />
      }
    >
      {isPending && <RowsSkeleton rows={3} />}

      {error && (
        <Empty error title="Could not load reminder tracking">
          {(error as Error).message}
        </Empty>
      )}

      {totals && (
        <section className="grid grid--4">
          <Stat
            label="Reminders sent"
            value={totals.sent.toLocaleString('en-IN')}
            icon={<WhatsAppIcon size={20} />}
            tone="var(--brand)"
            footnote={<span className="faint">{moneyExact(totals.spent)} charged</span>}
          />
          <Stat
            label="Opened the link"
            value={totals.clicked.toLocaleString('en-IN')}
            icon={<CartIcon size={20} />}
            tone="var(--info)"
            footnote={<span className="faint">{percent(totals.clickRate)} of sent</span>}
          />
          <Stat
            label="Ordered"
            value={totals.ordered.toLocaleString('en-IN')}
            icon={<CheckCircleIcon size={20} />}
            tone="var(--ok)"
            footnote={<span className="faint">{percent(totals.orderRate)} of sent</span>}
          />
          <Stat
            label="Revenue recovered"
            value={money(totals.revenue)}
            icon={<RupeeIcon size={20} />}
            tone="var(--warn)"
          />
        </section>
      )}

      {stores.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Store</th>
                <th className="right">Sent</th>
                <th className="right">Opened</th>
                <th className="right">Ordered</th>
                <th className="right">Revenue</th>
                <th className="right">Charged</th>
              </tr>
            </thead>
            <tbody>
              {stores.map((row) => (
                <tr key={row.shopId}>
                  <td>
                    <span>
                      {row.store}
                      <span className="table__sub">{row.domain}</span>
                    </span>
                  </td>
                  <td className="right">{row.sent.toLocaleString('en-IN')}</td>
                  <td className="right">
                    {row.clicked.toLocaleString('en-IN')} <span className="faint">({percent(row.clickRate, 0)})</span>
                  </td>
                  <td className="right">
                    {row.ordered.toLocaleString('en-IN')} <span className="faint">({percent(row.orderRate, 0)})</span>
                  </td>
                  <td className="right">{money(row.revenue)}</td>
                  <td className="right">{moneyExact(row.spent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totals && messages.length === 0 && (
        <Empty icon={<WhatsAppIcon size={24} />} title="No reminders yet">
          Nothing has been sent in this window. Reminders appear here as soon as one goes out.
        </Empty>
      )}

      {messages.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Sent</th>
                {scope === 'platform' && <th>Store</th>}
                <th>Phone</th>
                <th>Opened</th>
                <th>Order</th>
                <th>Checkout link</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((row) => (
                <tr key={row.id}>
                  <td>
                    {dateTime(row.sentAt)}
                    {row.status !== 'sent' && (
                      <span className="table__sub">
                        <Badge tone="bad">{row.status}</Badge>
                      </span>
                    )}
                  </td>
                  {scope === 'platform' && <td>{row.store}</td>}
                  <td>{row.phone ?? '—'}</td>
                  <td>
                    {row.clicked ? (
                      <span>
                        <Badge tone="ok">Opened{row.clicks > 1 ? ` ×${row.clicks}` : ''}</Badge>
                        <span className="table__sub">{dateTime(row.clickedAt)}</span>
                      </span>
                    ) : (
                      <span className="faint">Not yet</span>
                    )}
                  </td>
                  <td>
                    {row.ordered ? (
                      <span>
                        <Badge tone="ok">{row.orderNumber}</Badge>
                        <span className="table__sub">{money(row.orderValue)}</span>
                      </span>
                    ) : (
                      <span className="faint">No order</span>
                    )}
                  </td>
                  <td>
                    {row.link ? (
                      <a href={row.link} target="_blank" rel="noreferrer" className="link">
                        Open
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
